import { Api } from "@/api/worker"
import {
	PurchaseSuccess,
	PurchaseWaiting,
} from "@/components/screens/report/overview/PurchaseSuccess"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { useGetReport } from "@/queries/useReport"
import { useNavigate } from "@/router"
import { currentAuthAtom } from "@/state/auth"
import { useQuery } from "@tanstack/react-query"
import { useAnalytics } from "@/hooks/use-analytics"
import { useAtomValue } from "jotai"
import posthog from "posthog-js"
import React, { useState } from "react"

export default function StripeReturn() {
	const analytics = useAnalytics("Stripe_Return")
	const navigate = useNavigate()

	const auth = useAtomValue(currentAuthAtom)
	const sessionId = new URLSearchParams(window.location.search).get("session_id")
	const canceled = new URLSearchParams(window.location.search).get("canceled")
	const success = new URLSearchParams(window.location.search).get("success")
	const { refetch: getReport } = useGetReport()
	const [state, setState] = React.useState<
		"initial" | "waiting_confirmation" | "success" | "error"
	>("initial")

	const sessionQuery = useGetCheckoutSession(
		sessionId || "",
		!!sessionId && state === "waiting_confirmation"
	)

	React.useEffect(() => {
		const checkPayment = async () => {
			switch (state) {
				case "initial": {
					if (canceled)
						return navigate("/report/:id", { params: { id: auth.reportId } })

					if (success) {
						return setState("waiting_confirmation")
					}

					break
				}
				case "waiting_confirmation": {
					if (sessionQuery.isError) {
						console.error("Error fetching session", sessionQuery.error)
						return setState("error")
					}
					if (!sessionQuery.data) return

					if (sessionQuery.data.payment_status === "paid") {
						await getReport()
						analytics.sendEvent("Payment_Success")
						setState("success")
					}
					break
				}
				case "success":
					try {
						let email = sessionQuery.data?.customer_email
						if (email && posthog) posthog.identify(email, { email })
					} catch (e) {
						console.error("Error identifying user", e)
					}
					break
				case "error":
					break
			}
		}

		checkPayment()
	}, [
		state,
		success,
		sessionId,
		sessionQuery.error,
		sessionQuery.isError,
		sessionQuery.data,
	])

	const uuid = sessionQuery.data?.report_id || ""
	const email = sessionQuery.data?.customer_email || ""

	const handleContinue = () => {
		navigate("/report/:id", { params: { id: uuid } })
	}

	return (
		<Dialog open={!!success}>
			<DialogContent>
				{state === "waiting_confirmation" && <PurchaseWaiting />}
				{state === "success" && (
					<PurchaseSuccess
						continueText="Continue"
						email={email}
						onContinue={handleContinue}
						title="Hooray! 🎉"
						subtitle="Your report has been successfully purchased"
						description="You can now view and export the full version as a PDF."
						loginMessage={{
							text: "We've sent a login link to your email address:",
							highlightText: "a login link",
						}}
					/>
				)}
				{state === "error" && (
					<PurchaseSuccess
						continueText="Retry"
						email={email}
						onContinue={() => setState("waiting_confirmation")}
						title="Payment Error"
						subtitle="We couldn't process your payment"
						description="There was an error verifying your payment. Please try again or contact support if the issue persists."
						loginMessage={{
							text: "We couldn't process your payment",
							highlightText: "try again",
						}}
					/>
				)}
			</DialogContent>
		</Dialog>
	)
}

const useGetCheckoutSession = (sessionId: string, enabled: boolean) => {
	const [refetchInterval] = useState(() =>
		Math.floor(Math.random() * (4000 - 1000 + 1) + 1000)
	)
	return useQuery({
		queryKey: ["checkout-session", sessionId],
		queryFn: () => {
			return Api.getCheckoutSession({ query: { session_id: sessionId } })
		},
		enabled: enabled,
		refetchInterval: refetchInterval,
		retry: false,
	})
}
